import { useState, useEffect } from "react";
import axios from "axios";
import { Sparkles, X } from "lucide-react";
import { formatApiError } from "../contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { value: "idea", label: "Idée" },
  { value: "planned", label: "À venir" },
  { value: "in_progress", label: "En cours" },
  { value: "live", label: "Live (publié)" },
  { value: "bugfix", label: "Bug corrigé" },
];

export function UpdateFormModal({ open, onClose, onSaved, existing }) {
  const isEdit = Boolean(existing);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("live");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (open) {
      setTitle(existing?.title || "");
      setDescription(existing?.description || "");
      setStatus(existing?.status || "live");
      setError("");
    }
  }, [open, existing]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!title.trim() || !description.trim()) {
      setError("Le titre et la description sont requis.");
      return;
    }
    setLoading(true);
    try {
      const payload = { title: title.trim(), description: description.trim(), status };
      const { data } = isEdit
        ? await axios.put(`${API}/api/updates/${existing._id}`, payload, { withCredentials: true })
        : await axios.post(`${API}/api/updates`, payload, { withCredentials: true });
      onSaved?.(data);
      onClose();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4 animate-fade-in"
      onClick={onClose}
      data-testid="update-form-modal"
    >
      <form
        className="cbm-card w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        <div className="flex items-start justify-between p-5 border-b cbm-divider">
          <div className="flex items-center gap-2.5">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center"
              style={{ background: "rgba(201,162,39,0.15)", border: "1px solid rgba(201,162,39,0.3)" }}
            >
              <Sparkles size={16} className="text-gold" />
            </div>
            <div>
              <h2 className="font-outfit font-semibold text-white text-base leading-tight">
                {isEdit ? "Modifier la nouveauté" : "Ajouter une nouveauté"}
              </h2>
              <p className="text-cbm-muted text-xs mt-0.5">
                {isEdit ? "Mettez à jour le contenu" : "Communiquez l'évolution du produit"}
              </p>
            </div>
          </div>
          <button
            type="button"
            data-testid="close-update-form"
            onClick={onClose}
            className="text-cbm-muted hover:text-white p-1 transition-colors"
            aria-label="Fermer"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="cbm-label block mb-1.5">Titre</label>
            <input
              data-testid="update-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex : Nouveau système Prospects"
              className="cbm-input w-full"
              maxLength={120}
            />
          </div>

          <div>
            <label className="cbm-label block mb-1.5">Description</label>
            <textarea
              data-testid="update-description-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Décrivez la nouveauté en quelques lignes…"
              className="cbm-input w-full"
              rows={4}
              maxLength={500}
            />
          </div>

          <div>
            <label className="cbm-label block mb-1.5">Statut</label>
            <select
              data-testid="update-status-select"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="cbm-input w-full"
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            <p className="text-cbm-muted text-xs mt-1.5">
              La date de publication est enregistrée automatiquement pour les statuts <em>Live</em> et <em>Bug corrigé</em>.
            </p>
          </div>

          {error && (
            <div
              data-testid="update-form-error"
              className="px-3 py-2 rounded-lg text-sm text-red-300"
              style={{ background: "rgba(220,38,38,0.1)", border: "1px solid rgba(220,38,38,0.3)" }}
            >
              {error}
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              data-testid="cancel-update-form"
              onClick={onClose}
              className="cbm-btn-secondary text-sm flex-1 justify-center py-2.5"
            >
              Annuler
            </button>
            <button
              type="submit"
              data-testid="submit-update-form"
              disabled={loading}
              className="cbm-btn-primary text-sm flex-1 justify-center py-2.5 disabled:opacity-60"
            >
              {loading ? (isEdit ? "Enregistrement…" : "Publication…") : (isEdit ? "Enregistrer" : "Publier")}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
